import { takeEvery, takeLatest } from 'redux-saga';
import { take, call, put, fork, select } from 'redux-saga/effects';
import { saveAs } from 'file-saver/FileSaver';

import * as api from '~services/api'; // eslint-disable-line
import * as actions from '~actions'; // eslint-disable-line
import * as types from '~actiontypes'; // eslint-disable-line
import { getApiUrl, getClientUrl } from '~services/utils'; // eslint-disable-line
import {
  getActiveCompany,
  getEditedReport,
  getReport,
  getTilaajavastuuToken,
  getSession,
  getCurrentUser,
} from '~selectors'; // eslint-disable-line

/* eslint-disable func-names */

/* ***************************** TASKS *********************************** */

function* fetchReport(action) {
  const { country, identifier } = action.payload;
  try {
    const result = yield call(api.fetchReport, action.payload);
    const hasPiaced = localStorage.getItem(`${country}_${identifier}_piaced`);
    result.hasPiaced = hasPiaced;
    yield put(actions.receiveReport(result));
    // When we fetch a new report, set it as the editable report, too.
    const reportCopy = JSON.parse(JSON.stringify(result));
    yield put(actions.setReportEditable(reportCopy));
  } catch (err) {
    yield put(actions.fetchReportFail({ err }));
  }
  // if (setEditable) {
  //   console.debug('===> SET EDITABLE REPORT!!!');
  //   yield put(actions.setReportEditable({ ...data }));
  // }
}

function* setReportEditable() {
  const report = yield select(getReport);
  const reportCopy = JSON.parse(JSON.stringify(report));
  yield put(actions.setReportEditable(reportCopy));
}

function* saveReport() {
  // Get edited report data from store
  const edited = yield select(getEditedReport);
  const token = yield select(getTilaajavastuuToken);

  // TODO add edited report validation?

  try {
    const result = yield call(api.saveReport, edited, token);

    // Update edited report to store
    // TODO maybe use the data received from API call?
    yield put(actions.setReport(result));
    // Disable editing mode
    yield put(actions.clearEditing());
  } catch (err) {
    // TODO: Handle error
    console.error(err);
  }
}

function* fetchLocalities() {
  const result = yield call(api.fetchLocalities);
  yield put(actions.receiveLocalities(result));
}

function* fetchRegions() {
  const result = yield call(api.fetchRegions);
  yield put(actions.receiveRegions(result));
}

function* fetchReview(action) {
  // TODO show spinner
  try {
    const result = yield call(api.fetchReview, action.payload);
    yield put(actions.receiveReview(result.review));
  } catch (err) {
    if (err.status === 403 && action.payload.provider === 'zeckit') {
      localStorage.removeItem('zeckit_token');
    } else {
      throw err;
    }
  }
}

function* fetchReviewsByCompany(action) {
  // TODO show spinner

  const result = yield call(api.getCompanyReviews, action.payload);
  yield put(actions.receiveReviewsByCompany(result));
}

function* saveReview(action) {
  // TODO show spinner ?
  try {
    const result = yield call(api.saveReview, action.payload);
    yield put(actions.receiveReport({ review_average: result.review_average }));
  } catch (err) {
    yield put(actions.failReview(err));
  }
}

function* savePiace(action) {
  // TODO show spinner ?
  // TODO handle errors?

  try {
    const { country, identifier } = action.payload;
    const result = yield call(api.savePiace, action.payload);
    localStorage.setItem(`${country}_${identifier}_piaced`, true);

    yield put(actions.receiveReport({ hasPiaced: true }));
    yield put(actions.receivePiace(result));
  } catch (err) {
    yield put(actions.failPiace(err));
  }
}

function* fetchReviewsByUser(action) {
  // TODO show spinner
  const result = yield call(api.getUserReviews, action.payload);
  yield put(actions.receiveReviewsByUser(result));
}

function* tokenLogin(action) {
  let result = null;

  try {
    result = yield call(api.tokenLogin, action.payload);
  } catch (err) {
    if (err.status === 401) {
      // TODO: display error message?
      // It's fine, just don't log them in and remove the token
      localStorage.removeItem('user_token');
      yield put(actions.tokenLoginChecked());
      return;
    }

    throw err;
  }

  const { user } = result;
  yield put(
    actions.login({
      provider: 'tilaajavastuu',
      token: user.token,
      client: user,
    }),
  );
}

function* doLogin(action) {
  const { country, identifier, provider, token } = action.payload;
  yield put(actions.login({ provider, token }));

  if (country && identifier) {
    yield put(actions.fetchReview({ country, identifier, provider, token }));
  }
}

function* doLogout() {
  localStorage.removeItem('user_token');
  yield window.location.replace(`${getApiUrl()}/authenticate/logout`);
}

function* fetchAuthenticationForms(action) {
  const token = yield select(getTilaajavastuuToken);
  const result = yield call(
    api.fetchAuthenticationForms,
    action.payload,
    token,
  );

  yield put(actions.receiveAuthenticationForms(result));
}

function* fetchChatList() {
  const session = yield select(getSession);

  const { tokenLoginChecked } = session;
  if (!tokenLoginChecked) {
    yield take(types.LOGIN.TOKENCHECKED);
  }

  const token = yield select(getTilaajavastuuToken);

  let result = null;
  try {
    result = yield call(api.getChats, token);
  } catch (err) {
    return;
  }

  yield put(actions.receiveChatList({ chats: result.chats }));
}

function* fetchChat(action) {
  const session = yield select(getSession);

  const { tokenLoginChecked } = session;
  if (!tokenLoginChecked) {
    yield take(types.LOGIN.TOKENCHECKED);
  }

  const token = yield select(getTilaajavastuuToken);
  try {
    const result = yield call(api.getChatMessages, action.payload, token);
    yield put(
      actions.receiveChat({
        id: action.payload.id,
        company: result.company,
        review: result.review,
        messages: result.messages,
      }),
    );
  } catch (err) {
    if (err.status === 403) {
      if (action.payload.token) {
        yield put(
          actions.fetchChatFail({
            ...action.payload,
            reason: 'INVALID_TOKEN',
          }),
        );
      } else {
        const user = yield select(getCurrentUser);
        if (user) {
          const { company: { country, identifier } } = user;
          window.location.href =
            `${getClientUrl()}/selvitys/${country}/${identifier}/reviews`;
        } else {
          window.location.href = getClientUrl();
        }
      }
    } else {
      throw err;
    }
  }
}

function* saveChat(action) {
  const session = yield select(getSession);

  const { tokenLoginChecked } = session;
  if (!tokenLoginChecked) {
    yield take(types.LOGIN.TOKENCHECKED);
  }

  const token = yield select(getTilaajavastuuToken);
  yield call(api.addChatMessage, action.payload, token);
}

function* startChat(action) {
  const token = yield select(getTilaajavastuuToken);

  const result = yield call(api.startChat, action.payload, token);
  const { chat_id: chatId } = result;

  window.location.href = `${getClientUrl()}/selvitys/chat/${chatId}`;
}

function* checkPermission(action) {
  // Check tokenLoginChecked
  let session = yield select(getSession);

  const { country, identifier } = action.payload;
  const { tokenLoginChecked } = session;

  // If not then block until the login action is performed
  if (!tokenLoginChecked) {
    yield take(types.LOGIN.TOKENCHECKED);
  }

  // Fetch state again since it has changed
  session = yield select(getSession);

  // Here we can check session.logins.tilaajavastuu
  // and the company identifier match
  const { tilaajavastuu } = session.logins;

  if (
    !tilaajavastuu ||
    ((country !== tilaajavastuu.client.company.country ||
      identifier !== tilaajavastuu.client.company.identifier) &&
      !tilaajavastuu.client.super_user)
  ) {
    window.location.href = `${getApiUrl()}/authenticate/login`;
  }
}

function* fetchTopReviewers(action) {
  const result = yield call(api.fetchTopReviewers, action.payload);
  yield put(actions.receiveTopReviewers(result));
}

function* fetchTopCompanies(action) {
  const result = yield call(api.fetchTopCompanies, action.payload);
  yield put(actions.receiveTopCompanies(result));
}

function* fetchPortfolio({ payload: { identifier, country } }) {
  const result = yield call(api.fetchPortfolio, { identifier, country });

  if (!result.errors) {
    yield put(actions.receivePortfolio(result));
  } else {
    // TODO: handle errors

    // Init empty portfolio
    yield put(
      actions.receivePortfolio({
        company: { about: '', logo_id: null, portfolio_theme: 1 },
        slides: [],
      }),
    );
  }
}

function* savePortfolio(action) {
  const token = yield select(getTilaajavastuuToken);
  const result = yield call(api.savePortfolio, { ...action.payload, token });
  yield put(actions.savePortfolioSuccess(result));
}

function* uploadPortfolioImage({ payload }) {
  const token = yield select(getTilaajavastuuToken);
  const { file, slideId, country, identifier } = payload;
  const params = { country, identifier, token, file };
  const result = yield call(api.uploadPortfolioImage, params);

  if (result && result.id) {
    yield put(actions.receivePortfolioImage({ slideId, imgId: result.id }));
  }
}

function* uploadPortfolioCompanyLogo(action) {
  const token = yield select(getTilaajavastuuToken);
  const { file, country, identifier } = action.payload;
  const params = { country, identifier, token, file };
  const result = yield call(api.uploadPortfolioImage, params);

  if (result && result.id) {
    yield put(actions.receivePortfolioCompanyLogo(result.id));
  }
}

function* onEnablePortfolio(action) {
  const token = yield select(getTilaajavastuuToken);
  const result = yield call(api.enablePortfolio, { ...action.payload, token });

  if (result) {
    yield put(actions.receiveEnablePortfolio());
    yield put(actions.fetchPortfolio({ ...action.payload }));
  }
}

function* onDisablePortfolio(action) {
  const token = yield select(getTilaajavastuuToken);
  const result = yield call(api.disablePortfolio, { ...action.payload, token });

  if (!result.success) {
    yield put(actions.onEnablePortfolio({ ...action.payload }));
    console.error('ERROR disabling portfolio: ', result);
  }
}

function* onShowPortfolio(action) {
  // Optimistic update
  const report = yield select(getReport);
  report.portfolio_hidden = false;
  yield put(actions.receiveReport(report));

  const token = yield select(getTilaajavastuuToken);
  const result = yield call(api.showPortfolio, { ...action.payload, token });

  if (!result.success) {
    report.portfolio_hidden = true;
    yield put(actions.receiveReport(report));
    console.error('ERROR showing portfolio: ', result);
  }
}

function* onHidePortfolio(action) {
  // Optimistic update
  const report = yield select(getReport);
  report.portfolio_hidden = true;
  yield put(actions.receiveReport(report));

  const token = yield select(getTilaajavastuuToken);
  const result = yield call(api.hidePortfolio, { ...action.payload, token });

  if (!result.success) {
    report.portfolio_hidden = false;
    yield put(actions.receiveReport(report));
    console.error('ERROR hiding portfolio: ', result);
  }
}

function* fetchKeywords({ payload: { identifier, country } }) {
  const result = yield call(api.fetchKeywords, { identifier, country });
  if (result && result.keywords) {
    yield put(actions.receiveKeywords(result.keywords));
  } else {
    yield put(actions.receiveKeywords([]));
  }
}

function* saveKeywords(action) {
  const token = yield select(getTilaajavastuuToken);
  yield call(api.saveKeywords, {
    ...action.payload,
    token,
    keywords: action.payload.keywords.filter(x => x),
  });
}

function* fetchCompanyRegions({ payload: { identifier, country } }) {
  const result = yield call(api.fetchCompanyRegions, { identifier, country });
  yield put(actions.receiveCompanyRegions(result));
}

function* saveCompanyRegions(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    yield call(api.saveCompanyRegions, { ...action.payload, token });
  } catch (err) {
    console.error('saga: saveCompanyRegions', err);
    yield put(actions.failSaveCompanyRegions(
      { regions: action.payload.oldRegions }));
  }
}

function* activateLicense(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { license } =
      yield call(api.activateLicense, { ...action.payload, token });
    yield put(actions.activateLicenseSuccess({ ...action.payload, license }));
    // Update other resources
    const activeCompany = yield select(getActiveCompany);
    yield put(actions.fetchReport(activeCompany))
    yield put(actions.receiveEnablePortfolio());
    yield put(actions.fetchPortfolio(activeCompany));
  } catch (err) {
    console.error('saga: activateLicense', err);
    const payload = { error: 'Unkown reason.', license: null };
    if (err && err.body) {
      if (err.body.error) {
        payload.error = err.body.error;
      }
      if (err.body.license) {
        payload.license = err.body.license;
      }
    }
    yield put(actions.activateLicenseFail(payload));
  }
}

function* deactivateLicense(action) {
  try {
    const { payload: { license } } = action;
    const token = yield select(getTilaajavastuuToken);
    const { license: deactivatedLicense } =
      yield call(api.deactivateLicense, { ...action.payload, token });
    yield put(actions.deactivateLicenseSuccess({
      licenseId: license.id,
      license: deactivatedLicense,
    }));
    // Update other resources
    const activeCompany = yield select(getActiveCompany);
    yield put(actions.fetchReport(activeCompany));
  } catch (err) {
    console.error('saga: deactivateLicense', err);
    let error = 'Unkown reason.';
    if (err && err.body && err.body.error) {
      error = err.body.error;
    }
    yield put(actions.deactivateLicenseFail({ ...action.payload, error }));
  }
}

function* updateLicense(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { updatedLicense } =
      yield call(api.updateLicense, { ...action.payload, token });
    yield put(actions.updateLicenseSuccess({ updatedLicense }));
  } catch (err) {
    console.error('saga: updateLicense', err);
    let error = 'Unknown reason.';
    if (err && err.body && err.body.error) {
      error = err.body.error;
    }
    yield put(actions.updateLicenseFail({ ...action.payload, error }));
  }
}

function* upgradeLicense(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { license, newLicense } =
      yield call(api.upgradeLicense, { ...action.payload, token });
    yield put(actions.upgradeLicenseSuccess({
      ...action.payload, license, newLicense
    }));
  } catch (err) {
    console.error('saga: upgradeLicense', err);
    let error = 'Unknown reason.';
    if (err && err.body && err.body.error) {
      error = err.body.error;
    }
    yield put(actions.upgradeLicenseFail({ ...action.payload, error }));
  }
}

function* fetchLicenses(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { licenses, pagination } = yield call(
      api.fetchLicenses, { ...action.payload, token }
    );
    yield put(actions.receiveLicenses({ licenses, pagination }));
  } catch (err) {
    console.error('saga: fetchLicenses', err);
    yield put(actions.fetchLicensesFail());
  }
}

function* fetchCompanyLicenses(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { licenses } = yield call(
      api.fetchCompanyLicenses, { ...action.payload, token }
    );
    yield put(actions.receiveCompanyLicenses({ ...action.payload, licenses }));
  } catch (err) {
    console.error('saga: fetchCompanyLicenses', err);
    yield put(actions.fetchCompanyLicensesFail({ ...action.payload }));
  }
}

function* fetchLicenseLogs(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { licenseLogs, pagination } = yield call(
      api.fetchLicenseLogs, { ...action.payload, token }
    );
    yield put(actions.receiveLicenseLogs({ licenseLogs, pagination }));
  } catch (err) {
    console.error('saga: fetchLicenseLogs', err);
    yield put(actions.fetchLicenseLogsFail());
  }
}

function* fetchLicenseTypes(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { license_types: licenseTypes } =
      yield call(api.fetchLicenseTypes, { ...action.payload, token });
    yield put(actions.receiveLicenseTypes({ licenseTypes }));
  } catch (err) {
    console.error('saga: fetchLicenseTypes', err);
    yield put(actions.fetchLicenseTypesFail());
  }
}

function* fetchSellers(action) {
  try {
    let appendOrgInfo = true;
    if (action.payload && action.payload.appendOrgInfo !== undefined) {
      appendOrgInfo = action.payload.appendOrgInfo;
    }
    const token = yield select(getTilaajavastuuToken);
    const { sellers } = yield call(
      api.fetchSellers, { appendOrgInfo, token }
    );
    yield put(actions.receiveSellers({ sellers }));
  } catch (err) {
    console.error('saga: fetchSellers', err);
    yield put(actions.fetchSellersFail());
  }
}

function* exportLicenses(action) {
  try {
    const { payload: { type: filename } } = action;
    const token = yield select(getTilaajavastuuToken);
    const fileBlob = yield call(
      api.exportLicenses, { ...action.payload, token }
    );
    saveAs(fileBlob, `${filename}.xlsx`);
    yield put(actions.exportLicensesSuccess());
  } catch (err) {
    console.error('saga: exportLicenses', err);
    yield put(actions.exportLicensesFail());
  }
}

function* saveLicensePayment(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { ambigiousLogs, billedLogs, paidLogs } = yield call(
      api.saveLicensePayment, { ...action.payload, token }
    );
    yield put(actions.saveLicensePaymentSuccess({
      ambigiousLogs, billedLogs, paidLogs
    }));
  } catch (err) {
    console.error('saga: saveLicensePayment', err);
    yield put(actions.saveLicensePaymentFail());
  }
}

function* fetchLicensePayments(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { payments } = yield call(
      api.fetchLicensePayments, { ...action.payload, token }
    );
    yield put(actions.receiveLicensePayments({ logs: payments }));
  } catch (err) {
    console.error('saga: fetchLicensePayments', err);
    yield put(actions.fetchLicensePaymentsFail());
  }
}

function* importLicenses(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { created, invalidRows } = yield call(
      api.importLicenses, { ...action.payload, token }
    );
    yield put(actions.importLicensesSuccess({ created, invalidRows }));
  } catch (err) {
    console.error('saga: migrateOldLicenses', err);
    yield put(actions.importLicensesFail());
  }
}

function* fetchContactPersons(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { contactPersons } = yield call(
      api.fetchContactPersons, { ...action.payload, token }
    );
    yield put(actions.receiveContactPersons({
      ...action.payload, contactPersons,
    }));
  } catch (err) {
    console.error('saga: fetchContactPersons', err);
    yield put(actions.fetchContactPersonsFail({ ...action.payload }));
  }
}

function* addContactPerson(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { contactPerson } = yield call(
      api.addContactPerson, { ...action.payload, token }
    );
    yield put(actions.addContactPersonSuccess({
      ...action.payload, contactPerson,
    }));
  } catch (err) {
    console.error('saga: addContactPerson', err);
    const payload = { ...action.payload, error: 'Unkown reason.' };
    if (err && err.body) {
      if (err.body.error) {
        payload.error = err.body.error;
      }
    }
    yield put(actions.addContactPersonFail(payload));
  }
}

function* updateContactPerson(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const { contactPerson } = yield call(
      api.updateContactPerson, { ...action.payload, token }
    );
    yield put(actions.updateContactPersonSuccess({
      ...action.payload, contactPerson,
    }));
  } catch (err) {
    console.error('saga: updateContactPerson', err);
    yield put(actions.updateContactPersonFail({ ...action.payload }));
  }
}

function* fetchRPQuickCheck(action) {
  try {
    const { quickCheck } = yield call(
      api.fetchRPQuickCheck, { ...action.payload }
    );
    yield put(actions.fetchRPQuickCheckRecieve({
      ...action.payload, quickCheck
    }));
  } catch (err) {
    console.error('saga: fetchRPQuickCheck', err);
    yield put(actions.fetchRPQuickCheckFail({ ...action.payload }));
  }
}

function* fetchCLReport(action) {
  try {
    const token = yield select(getTilaajavastuuToken);
    const fileBlob = yield call(
      api.fetchCLReport, { ...action.payload, token }
    );
    saveAs(fileBlob, `${action.payload.identifier}.cl.pdf`);
  } catch (err) {
    console.error('saga: fetchCLReport', err);
  }
}


/* ***************************** /TASKS *********************************** */

/* ***************************** WATCHERS *********************************** */
/* eslint-disable no-constant-condition */

// Watches page changes, triggers required data prefetch loading actions.
// Also does query param checking for prefilling stuff. This is done here
// "manually" as a workaround due to the still persistent sad state of routing
// in React. The recommended router with redux is react-router-redux and it
// seems not to be safe to use for this purpose. redux-router would be better
// but changing to it seems like asking for trouble right now.

function* watchStart() {
  while (true) {
    yield take(types.START);

    yield put(actions.fetchLocalities());
    yield put(actions.fetchRegions());

    const token = localStorage.getItem('user_token');
    yield put(actions.onTokenLogin({ token }));
    yield take(types.CLIENT.SET);

    yield put(actions.tokenLoginChecked());
  }
}

function* watchPermission() {
  yield* takeEvery(types.PERMISSION, checkPermission);
}

// DEPRECATED as of react-router v4 update
// function* watchNavigate() {
//   while (true) {
//     const { payload: pathname } = yield take(types.NAVIGATE);
//     yield call(browserHistory.push, pathname);
//   }
// }

function* watchFetchReport() {
  yield* takeEvery(types.REPORT.FETCH, fetchReport);
}

function* watchSetEditingMode() {
  yield* takeEvery(types.EDITING.SET, setReportEditable);
}

function* watchSaveReport() {
  yield* takeEvery(types.REPORT.SAVE, saveReport);
}

function* watchFetchLocalities() {
  yield* takeEvery(types.LOCALITIES.FETCH, fetchLocalities);
}

function* watchFetchRegions() {
  yield* takeEvery(types.REGIONS.FETCH, fetchRegions);
}

function* watchFetchReviewsByCompany() {
  yield* takeEvery(types.COMPANY_REVIEWS.FETCH, fetchReviewsByCompany);
}

function* watchFetchReviewsByUser() {
  yield* takeEvery(types.USER_REVIEWS.FETCH, fetchReviewsByUser);
}

function* watchSaveReview() {
  yield* takeEvery(types.REVIEW.SAVE, saveReview);
}

function* watchSavePiace() {
  yield* takeEvery(types.PIACE.SAVE, savePiace);
}

function* watchFetchReview() {
  yield* takeEvery(types.REVIEW.FETCH, fetchReview);
}

function* watchLogin() {
  yield* takeEvery(types.LOGIN.CLIENT, doLogin);
}

function* watchLogout() {
  yield* takeEvery(types.CLIENT.CLEAR, doLogout);
}

function* watchTokenLogin() {
  yield* takeEvery(types.LOGIN.TOKEN, tokenLogin);
}

function* watchFetchAuthenticationForms() {
  yield* takeEvery(types.AUTH.FETCH, fetchAuthenticationForms);
}

function* watchDebug() {
  yield* takeEvery(types.DEBUG, action =>
    console.debug('REDUX DEBUG', action.payload),
  );
}

function* watchFetchChatList() {
  yield* takeEvery(types.CHAT.LIST, fetchChatList);
}

function* watchFetchChat() {
  yield* takeEvery(types.CHAT.FETCH, fetchChat);
}

function* watchSaveChat() {
  yield* takeEvery(types.CHAT.SAVE, saveChat);
}

function* watchStartChat() {
  yield* takeEvery(types.CHAT.START, startChat);
}

function* watchFetchTopReviewers() {
  yield* takeEvery(types.TOP_REVIEWERS.FETCH, fetchTopReviewers);
}

function* watchFetchTopCompanies() {
  yield* takeEvery(types.TOP_COMPANIES.FETCH, fetchTopCompanies);
}

function* watchFetchPortfolio() {
  yield* takeEvery(types.PORTFOLIO.FETCH, fetchPortfolio);
}

function* watchSavePortfolio() {
  yield* takeEvery(types.PORTFOLIO.SAVE, savePortfolio);
}

function* watchUploadPortfolioImage() {
  yield* takeEvery(types.PORTFOLIO.UPLOAD_IMAGE, uploadPortfolioImage);
}

function* watchUploadPortfolioCompanyLogo() {
  yield* takeEvery(types.PORTFOLIO.UPLOAD_LOGO, uploadPortfolioCompanyLogo);
}

function* watchOnEnablePortfolio() {
  yield* takeEvery(types.PORTFOLIO.ENABLE, onEnablePortfolio);
}

function* watchOnDisablePortfolio() {
  yield* takeEvery(types.PORTFOLIO.DISABLE, onDisablePortfolio);
}

function* watchOnShowPortfolio() {
  yield* takeEvery(types.PORTFOLIO.SHOW, onShowPortfolio);
}

function* watchOnHidePortfolio() {
  yield* takeEvery(types.PORTFOLIO.HIDE, onHidePortfolio);
}

function* watchSaveKeywords() {
  yield* takeLatest(types.PAID.SAVE_KEYWORDS, saveKeywords);
}

function* watchFetchKeywords() {
  yield* takeEvery(types.PAID.FETCH_KEYWORDS, fetchKeywords);
}

function* watchSaveCompanyRegions() {
  yield* takeLatest(types.PAID.SAVE_COMPANY_REGIONS, saveCompanyRegions);
}

function* watchFetchCompanyRegions() {
  yield* takeEvery(types.PAID.FETCH_COMPANY_REGIONS, fetchCompanyRegions);
}

function* watchActivateLicense() {
  yield* takeEvery(types.LICENSE.SAVE, activateLicense);
}

function* watchDeactivateLicense() {
  yield* takeEvery(types.LICENSE.DEACTIVATE, deactivateLicense);
}

function* watchUpdateLicense() {
  yield* takeEvery(types.LICENSE.UPDATE, updateLicense);
}

function* watchUpgradeLicense() {
  yield* takeEvery(types.LICENSE.UPGRADE, upgradeLicense);
}

function* watchFetchLicenses() {
  yield* takeEvery(types.LICENSES.FETCH, fetchLicenses);
}

function* watchFetchCompanyLicenses() {
  yield* takeEvery(types.COMPANY_LICENSES.FETCH, fetchCompanyLicenses);
}

function* watchFetchLicenseLogs() {
  yield* takeEvery(types.LICENSE_LOGS.FETCH, fetchLicenseLogs);
}

function* watchFetchSellers() {
  yield* takeEvery(types.SELLERS.FETCH, fetchSellers);
}

function* watchFetchLicenseTypes() {
  yield* takeEvery(types.LICENSE_TYPES.FETCH, fetchLicenseTypes);
}

function* watchExportLicenses() {
  yield* takeEvery(types.LICENSES.EXPORT, exportLicenses);
}

function* watchSaveLicensePayment() {
  yield* takeEvery(types.LICENSE_PAYMENTS.SAVE, saveLicensePayment);
}

function* watchFetchLicensePayments() {
  yield* takeEvery(types.LICENSE_PAYMENTS.FETCH, fetchLicensePayments);
}

function* watchImportLicenses() {
  yield* takeEvery(types.LICENSES.IMPORT, importLicenses);
}

function* watchFetchContactPersons() {
  yield* takeEvery(types.CONTACT_PERSONS.FETCH, fetchContactPersons);
}

function* watchAddContactPerson() {
  yield* takeEvery(types.CONTACT_PERSON.ADD, addContactPerson);
}

function* watchUpdateContactPerson() {
  yield* takeEvery(types.CONTACT_PERSON.UPDATE, updateContactPerson);
}

function* watchFetchRPQuickCheck() {
  yield* takeEvery(types.RP_QUICK_CHECK.FETCH, fetchRPQuickCheck);
}

function* watchFetchCLReport() {
  yield* takeEvery(types.CL_REPORT.FETCH, fetchCLReport);
}

/* ***************************** /WATCHERS ********************************** */

export default function* root() {
  yield fork(watchStart);
  yield fork(watchPermission);
  // yield fork(watchNavigate);
  yield fork(watchDebug);
  yield fork(watchLogin);
  yield fork(watchLogout);
  yield fork(watchTokenLogin);
  yield fork(watchSetEditingMode);
  yield fork(watchFetchReport);
  yield fork(watchFetchLocalities);
  yield fork(watchFetchRegions);
  yield fork(watchSaveReview);
  yield fork(watchFetchReview);
  yield fork(watchFetchReviewsByCompany);
  yield fork(watchFetchReviewsByUser);
  yield fork(watchSavePiace);
  yield fork(watchSaveReport);
  yield fork(watchFetchChatList);
  yield fork(watchFetchChat);
  yield fork(watchSaveChat);
  yield fork(watchStartChat);
  yield fork(watchFetchAuthenticationForms);
  yield fork(watchFetchTopReviewers);
  yield fork(watchFetchTopCompanies);
  yield fork(watchFetchPortfolio);
  yield fork(watchSavePortfolio);
  yield fork(watchUploadPortfolioImage);
  yield fork(watchUploadPortfolioCompanyLogo);
  yield fork(watchOnEnablePortfolio);
  yield fork(watchOnDisablePortfolio);
  yield fork(watchOnShowPortfolio);
  yield fork(watchOnHidePortfolio);
  yield fork(watchSaveKeywords);
  yield fork(watchFetchKeywords);
  yield fork(watchSaveCompanyRegions);
  yield fork(watchFetchCompanyRegions);
  yield fork(watchActivateLicense);
  yield fork(watchDeactivateLicense);
  yield fork(watchUpdateLicense);
  yield fork(watchUpgradeLicense);
  yield fork(watchFetchLicenses);
  yield fork(watchFetchCompanyLicenses);
  yield fork(watchFetchLicenseLogs);
  yield fork(watchFetchLicenseTypes);
  yield fork(watchFetchSellers);
  yield fork(watchExportLicenses);
  yield fork(watchSaveLicensePayment);
  yield fork(watchFetchLicensePayments);
  yield fork(watchImportLicenses);
  yield fork(watchFetchContactPersons);
  yield fork(watchAddContactPerson);
  yield fork(watchUpdateContactPerson);
  yield fork(watchFetchRPQuickCheck);
  yield fork(watchFetchCLReport);
}
